/**
 * API functions for SQLFlow Deploy
 */
import { customFetch } from '../../utils/api';
import type {
  Machine, MachineInput, EmailTemplate, VersionInfo, ApiResponse,
  SelfServiceLink, SelfServiceLinkInput, SelfServiceLinkCheckResult, SelfServiceRedeemResult,
  TrialLicenseInput,
} from './types';

const BASE_URL = '/api/sqlflow-deploy';

export const machineApi = {
  /**
   * List all machines with optional search
   */
  list: async (search?: string): Promise<ApiResponse<Machine>> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    const response = await customFetch(`${BASE_URL}/machines/?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to fetch machines');
    }
    return response.json();
  },

  /**
   * Get a single machine by ID
   */
  get: async (id: number): Promise<Machine> => {
    const response = await customFetch(`${BASE_URL}/machines/${id}/`);
    if (!response.ok) {
      throw new Error('Failed to fetch machine');
    }
    return response.json();
  },

  /**
   * Create a new machine
   */
  create: async (data: MachineInput): Promise<Machine> => {
    const response = await customFetch(`${BASE_URL}/machines/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create machine');
    }
    return response.json();
  },

  /**
   * Update an existing machine
   */
  update: async (id: number, data: MachineInput): Promise<Machine> => {
    const response = await customFetch(`${BASE_URL}/machines/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to update machine');
    }
    return response.json();
  },

  /**
   * Delete a machine
   */
  delete: async (id: number): Promise<void> => {
    const response = await customFetch(`${BASE_URL}/machines/${id}/`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete machine');
    }
  },

  /**
   * Download license package (ZIP)
   */
  downloadUrl: (id: number): string => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    return `${apiUrl}${BASE_URL}/machines/${id}/download/`;
  },

  /**
   * Download license file only
   */
  licenseUrl: (id: number): string => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    return `${apiUrl}${BASE_URL}/machines/${id}/license/`;
  },

  /**
   * Upload license to gudusoft.com
   */
  upload: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await customFetch(`${BASE_URL}/machines/${id}/upload/`, {
      method: 'POST',
    });
    return response.json();
  },

  /**
   * Get email template preview
   */
  getEmailPreview: async (id: number): Promise<EmailTemplate> => {
    const response = await customFetch(`${BASE_URL}/machines/${id}/email-preview/`);
    if (!response.ok) {
      throw new Error('Failed to fetch email preview');
    }
    return response.json();
  },

  /**
   * Send license email to the machine's contact
   */
  sendEmail: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await customFetch(`${BASE_URL}/machines/${id}/send-email/`, {
      method: 'POST',
    });
    return response.json();
  },
};

export const selfServiceApi = {
  /** Admin: create a new self-service link */
  create: async (data: SelfServiceLinkInput): Promise<SelfServiceLink> => {
    const response = await customFetch(`${BASE_URL}/self-service-links/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || JSON.stringify(error) || 'Failed to create link');
    }
    return response.json();
  },

  /** Admin: list all self-service links */
  list: async (): Promise<ApiResponse<SelfServiceLink>> => {
    const response = await customFetch(`${BASE_URL}/self-service-links/`);
    if (!response.ok) throw new Error('Failed to fetch links');
    return response.json();
  },

  /** Public: check if a link is still valid (no auth) */
  check: async (linkId: string): Promise<SelfServiceLinkCheckResult> => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    const response = await fetch(`${apiUrl}${BASE_URL}/self-service/${linkId}/check/`);
    return response.json();
  },

  /** Public: redeem a link (no auth) */
  redeem: async (linkId: string, data: MachineInput): Promise<SelfServiceRedeemResult> => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    const response = await fetch(`${apiUrl}${BASE_URL}/self-service/${linkId}/redeem/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to redeem link');
    }
    return response.json();
  },
};

export const trialLicenseApi = {
  /** Public: create a trial license (no auth, no link) */
  create: async (data: TrialLicenseInput): Promise<SelfServiceRedeemResult> => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    const response = await fetch(`${apiUrl}${BASE_URL}/trial-license/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create trial license');
    }
    return response.json();
  },
};

export const utilityApi = {
  /**
   * Get latest SQLFlow version info
   */
  getSync: async (): Promise<VersionInfo> => {
    const response = await customFetch(`${BASE_URL}/sync/`);
    if (!response.ok) {
      throw new Error('Failed to fetch version info');
    }
    return response.json();
  },

  /**
   * Get list of database vendors
   */
  getDatabaseVendors: async (): Promise<{ vendors: string[] }> => {
    const response = await customFetch(`${BASE_URL}/database-vendors/`);
    if (!response.ok) {
      throw new Error('Failed to fetch database vendors');
    }
    return response.json();
  },
};
